// src/App.tsx
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react'
import MessageInput from './components/MessageInput'
import MessageList from './components/MessageList'
import { useOpenStream } from './hooks/useOpenStream'
import { useSendMessage } from './hooks/useSendMessage'
import { useStartConversation } from './hooks/useStartConversation'
import logo from './logo.png'
import './App.css'
import CitationsList from './components/CitationsList'
import { currentUser } from './lib/auth'
import { useAddUserActivity } from './hooks/db/useAddUserActivity'
import { useUserQuotaCheck } from './hooks/db/useUserQuotaCheck'
import Modal from './components/ui/Modal'
import { useAddMessage } from './hooks/db/useAddMessage'
import { useSearchParams } from 'react-router-dom'
import { useGetConversation } from './hooks/db/useGetConversation'
import { transformMessageObject } from './lib/common'
import { useLoading } from './contexts/LoadingContext'
import { useAddCitation } from './hooks/db/useAddCitation'


export type ChatMessage = {
  id: string
  data: string
  sender: 'user' | 'bot'
  messageId: string
  conversationId: string
  fromDB?: boolean // Optional property to indicate if the message is from the database
}

export type Citation = {
  chunkId: string
  filename: string
  contents: string
}

function genId() {
  return typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function App() {
  const [conversationId, setConversationId] = useState('')
  const [userId, setUserId]                 = useState('')
  const [userInternalId, setUserInternalId] = useState('')
  const [userJWT, setUserJWT]               = useState('')
  const [canSendMessage, setCanSendMessage] = useState(true)
  const [selectedConversationId, setSelectedConversationId] = useState('') // selected conversation id from the chat history sidebar

  const [searchParams] = useSearchParams()
  const { setLoading } = useLoading()
  const c = searchParams.get('c') // get the conversation id from URL params


  useEffect(() =>{
    console.log("============ Inside APP.tsx useEffect====================")
    const user = currentUser()
    if(user){
      console.log("App.tsx is loaded - User: ", user)
      // checking with user email id as botpress user name
      setUserId(user.email) 
      initConversation(user.email)
    }
  }, [])
  
  // are we in the expanded view mode (i.e. after user submits the first question)
  const [fullScreenView, setFullScreenView]     = useState(false)
  
  const [initComplete, setInitComplete]     = useState(false)
  
  // set up hooks
  const { startConversation } = useStartConversation()
  const { openStream, messages, setMessages, streamOpen, citations } = useOpenStream()
  const { sendMessage } = useSendMessage()
  const { addUserActivity } = useAddUserActivity()
  const { getUserQuotaUsage } = useUserQuotaCheck()
  const { getConversation } = useGetConversation()
  const { addCitation } = useAddCitation()
  

  const [showModal, setShowModal]     = useState(false)
  const [modalBody, setModalBody]     = useState('')
  const [modalTitle, setModalTitle]   = useState('')
  const [quotaUsageErrorMsg, setQuotaUsageErrorMsg]   = useState('')
  const { addMessage } = useAddMessage()

  
  // initiate conversation -> start stream -> set to research mode
  const initConversation = async (userId: string) => {
    if(userId){
      console.log("starting conversation for user - ", userId)
      const startResponse = await startConversation(userId)
      const startData = await startResponse?.json()
      setUserJWT(startData.userKey)
      setUserInternalId(startData.userInternalId)
      setConversationId(startData.convoKey)
      setInitComplete(true)
    }
  }

  useEffect(() => {
    console.log("=== streamopen changed: ===", streamOpen)
  }, [streamOpen])
  useEffect(() => {
    console.log("=== initcomplete changed: ===", initComplete)
  }, [initComplete])
  useEffect(() => {
    console.log("=== userJWT changed: ===", userJWT)
  }, [userJWT])
  useEffect(() => {
    console.log("=== convoId changed: ===", conversationId)
  }, [conversationId])
  useEffect(() => {
    console.log("=== userInternalId changed: ===", userInternalId)
  }, [userInternalId])
  

  // trigger sending a message
  const handleSend = async (text: string) => {

    if (!openStream) return

    // on first submit, mark expanded state 
    if (!fullScreenView) {
      setFullScreenView(true)
    }

    //Before sending a message, check the user quota
    const quotaUsage = await getUserQuotaUsage()

    if(quotaUsage && !quotaUsage.canSendMessage){
      setModalBody(quotaUsage.message)
      setModalTitle('Quota Limit Reached!')
      setShowModal(true)
      setCanSendMessage(false)
      setQuotaUsageErrorMsg(quotaUsage.message)
      return
    }else{
      setCanSendMessage(true)
      setQuotaUsageErrorMsg('')
    }
    
    console.log("==== STREAMOPEN? ====", streamOpen)

    if (streamOpen === false)
      openStream(conversationId, userInternalId, userId)

    // send message
    await sendMessage(conversationId, userJWT, text)

    setMessages((m) => [
      ...m,
      { id: genId(), data: text, sender: 'user', conversationId: conversationId },
    ])


     // Log user activity
     await addUserActivity('QUERY', {text, conversationId})

  }

  // handle new messages being added to the messageList
  useEffect(() => {
    const handleMessages = async () => {
      if (messages.length === 0) return
      // find the last message added
      const lastMessage = messages[messages.length - 1]
      if(lastMessage && lastMessage.fromDB !== undefined && lastMessage.fromDB === true){
        return // if the message is from the database, we don't need to add it again
      }
      if(lastMessage){
        let conversationId = lastMessage.conversationId
        if(selectedConversationId){
          conversationId = selectedConversationId
        }
        await addMessage(conversationId, lastMessage.id, lastMessage.sender, lastMessage.data)
      }
    }
    handleMessages()
  }, [messages])

  // handle new citations being added to the list
  useEffect(() => {
    const handleCitations = async () => {
      if (citations.length === 0) return
      
      // find the last citation added
      const lastCitation = citations[citations.length - 1]
      if(lastCitation){
      // filter citations to only include those newly added in this session
      const filteredCitations = citations.filter((citation, index) => 
        citation.messageId === lastCitation.messageId && citations.indexOf(citation) === index
      )

      let conversationId = lastCitation.conversationId
        const messageId = lastCitation.messageId
        if(selectedConversationId){
          conversationId = selectedConversationId
        }
        if (conversationId && messageId && filteredCitations.length > 0) {
          await addCitation(conversationId, messageId, filteredCitations)
          // Add citations to the corresponding message object
          setMessages((prevMessages) =>
            prevMessages.map((message) =>
              message.id === messageId
              ? { ...message, citations: filteredCitations }
              : message
            )
          )
        } else {
          console.error("Conversation ID is undefined. Cannot add citation.")
        }
      }
    }
    handleCitations()
  }, [citations])

  useEffect(() => {
    const getConversationObject = async () => {
      if (c) {
        try {
          setLoading(true)
          const response = await getConversation(c)
          const data = await response?.json()
          
          if (data && data.conversation) {
            setSelectedConversationId(data.conversation.botpressId)
            const messages = transformMessageObject(data.conversation.messages || [])
            setMessages(messages as ChatMessage[])
            setFullScreenView(true)
          } else {
            console.error("No conversation found for id:", c)
          }
        } catch (error) {
          console.error("Error fetching conversation:", error)
        } finally {
          setLoading(false)
        }
      }else{
        console.log("No conversation id provided in URL params, setting to empty")
        setSelectedConversationId('')
        setMessages([] as ChatMessage[])
        setFullScreenView(false)
      }
    }
    getConversationObject()
  }, [c])

  return (
      <>
        <div className="mobileBlocker">This application is optimized for browsers with a width of at least 1000 pixels. Please adjust your browser's zoom settings or your computer's screen size to reveal the app.</div>
        <div className={`App ${fullScreenView ? 'expanded' : 'collapsed'}`}>
          {/* header: logo + input */}
          <div className="header">
              <img src={logo}
                alt="Logo"
                className="logo"
                />
              <MessageInput onSend={handleSend} canSend={!streamOpen} initComplete={initComplete} quotaUsageErrorMsg={quotaUsageErrorMsg} />
          </div>
          
          {/* chat area: hidden until first submit */}


          <div className="chat-container" >

            <MessageList messages={messages as ChatMessage[]} canSend={!streamOpen} />

            {/* {citations.length > 0 &&
              <CitationsList citations={citations as Citation[]} />
            } */}
          </div>

          <Modal
            isOpen={showModal}
            title={modalTitle}
            onClose={() => setShowModal(false)}
            body={modalBody}
          />
        </div>
      </>
  )
}

export default App
